import { Injectable } from '@angular/core';
import { Observable, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommonDefaults } from '@windsim/core/configs';
import { UserService } from '@windsim/modules/user/services';
import { ProjectService } from './project.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectLimitationService {
  constructor(private readonly userService: UserService, private readonly projectService: ProjectService) {}

  /**
   * Gets the maximum number of projects allowed for the current user
   */
  public get maximumProjectsLimit(): number {
    // No limit for other users
    return this.userService.isEvaluationUser ? CommonDefaults.MAXIMUM_PROJECTS_EVALUATION_USER : Number.MAX_SAFE_INTEGER;
  }

  /**
   * Checks if the current user can create a new project
   */
  public get canCreateProject$(): Observable<boolean> {
    if (!this.userService.isEvaluationUser) {
      return of(true); // No limit for non-evaluation users
    }

    return this.projectService.projectsCounter$.pipe(map((currentCount) => currentCount < this.maximumProjectsLimit));
  }

  /**
   * Gets the current project count and limit information
   */
  public get projectLimitInfo$(): Observable<{ currentCount: number; maxLimit: number; canCreate: boolean }> {
    return combineLatest([this.projectService.projectsCounter$, this.canCreateProject$]).pipe(
      map(([currentCount, canCreate]) => ({
        currentCount,
        maxLimit: this.maximumProjectsLimit,
        canCreate,
      })),
    );
  }

  /**
   * Gets a user-friendly message about project limitations
   */
  public getProjectLimitMessage(): string {
    if (!this.userService.isEvaluationUser) {
      return '';
    }
    return `As an evaluation user, you can create up to ${this.maximumProjectsLimit} projects.`;
  }
}
